/* eslint-disable max-len */
import React, {
  useState, useEffect, useLayoutEffect, useRef,
} from 'react';
import { Tooltip } from '@mui/material';
import { ChevronLeft, ChevronRight, Info } from 'lucide-react';
import PropTypes from 'prop-types';
import Avatar from '../../components/avatar';
import { calculateTimeSummary } from '../../utils/activity';

const softShadow = 'shadow-[0_1px_2px_rgba(14,30,64,0.04)]';

function ringColorFor(engagement) {
  if (engagement >= 70) return '#6b9f5f';
  if (engagement >= 40) return '#F19235';
  return '#b94f3c';
}

function TeamMemberCard({ team }) {
  const ref = useRef();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [mates, setMates] = useState([]);

  useLayoutEffect(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const size = Math.max(1, Math.floor(rect.width / 72));
    setPageSize(size * 2);
    if (team?.teamMates?.length) {
      const length = team?.teamMates?.length ?? 0;
      setTotalPage(Math.max(1, Math.ceil(length / size / 2)));
    }
  });

  useEffect(() => {
    if (team?.teamMates?.length) {
      const currentMates = team.teamMates.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize,
      );
      setMates(currentMates);
      const length = team?.teamMates?.length ?? 0;
      setTotalPage(Math.max(1, Math.ceil(length / pageSize)));
    }
  }, [team, currentPage, pageSize]);

  const nextPage = () => {
    if (currentPage < totalPage) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const memberCount = team?.teamMates?.length ?? 0;

  return (
    <div className={`rounded-xl bg-white border border-surface-2 ${softShadow} p-5 h-full`}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-xs uppercase tracking-wider text-ink-500 font-semibold inline-flex items-center gap-1.5">
          Team Members:
          {' '}
          <span className="text-ink-900 ml-1">{memberCount}</span>
          <Tooltip
            arrow
            placement="right"
            title={`Your team comprises ${memberCount} individuals,
              a crucial factor for effective collaboration and project management.`}
          >
            <span className="inline-flex items-center justify-center text-ink-500 cursor-help">
              <Info size={14} />
            </span>
          </Tooltip>
        </p>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Previous members"
            disabled={currentPage === 1}
            onClick={prevPage}
            className="w-7 h-7 rounded-md text-ink-500 hover:bg-surface-1 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed flex items-center justify-center transition"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            aria-label="Next members"
            disabled={currentPage === totalPage}
            onClick={nextPage}
            className="w-7 h-7 rounded-md text-ink-500 hover:bg-surface-1 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed flex items-center justify-center transition"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
      <div ref={ref} className="flex flex-wrap gap-x-3 gap-y-4">
        {mates?.length > 0 && mates.map((mate) => {
          const memberName = `${mate.member?.user.firstName ?? ''} ${mate.member?.user.lastName ?? ''}`.trim();
          const { totalSeconds, activeSeconds } = calculateTimeSummary(mate.member?.activity);
          const totalActivityPercentage = Math.round((activeSeconds * 100) / totalSeconds || 0);
          const borderColor = ringColorFor(totalActivityPercentage);
          const backgroundColor = '#e8eef7';
          const color = '#3d5a8a';
          return (
            <Tooltip
              key={mate.memberId}
              placement="bottom"
              title={`${memberName} - ${totalActivityPercentage}%`}
            >
              <div>
                <Avatar
                  src={mate.member?.user.profilePicture}
                  name={(mate.member && mate.member.user.firstName ? mate.member.user.firstName[0] : 'N')
                    + (mate.member && mate.member.user.lastName ? mate.member.user.lastName[0] : 'A')}
                  width={48}
                  height={48}
                  color={color}
                  borderColor={borderColor}
                  backgroundColor={backgroundColor}
                  outline
                />
              </div>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}

TeamMemberCard.propTypes = {
  team: PropTypes.shape({
    teamMates: PropTypes.arrayOf(
      PropTypes.shape({
        memberId: PropTypes.number.isRequired,
        member: PropTypes.shape({
          user: PropTypes.shape({
            firstName: PropTypes.string.isRequired,
            lastName: PropTypes.string.isRequired,
            profilePicture: PropTypes.string,
          }),
        }),
      }),
    ),
  }),
};

export default TeamMemberCard;
